// Journey progress storage helpers — FU6.
import { authAPI } from '../services/api';

// Storage shape (v5):
//   { schemaVersion: 5, steps: { foundation: true, team: true, roster: true, ... } }
// v5: adds the Essentials coaching-cycle milestones while preserving all v4 slugs.
// v4 (R220): added 'philosophy' as the new first step. Migration grandfathers
//   existing users who already completed 'team' by backfilling philosophy: true,
//   so they don't see the journey re-open with the new step marked incomplete.
// v3 (R211 #261): refreshed milestone copy for match/chat/calendar — same step IDs.
// v2: { schemaVersion: 2, steps: { ... } }
// Legacy (v1): numeric keys '1'..'10' at the top level. Migrated once on module load.

export const STORAGE_KEY = 'eliteedge_journey_progress';
export const PENDING_STORAGE_KEY = 'eliteedge_journey_pending';
export const CURRENT_SCHEMA = 5;

export const LEGACY_TO_SLUG = {
  '1': 'team',
  '2': 'roster',
  '3': 'photos',
  '4': 'match',
  '5': 'training',
  '6': 'drill',
  '7': 'tactics',
  '8': 'chat',
  '9': 'calendar',
  '10': 'film'
};

function defaultStorage() {
  return typeof localStorage !== 'undefined' ? localStorage : null;
}

function currentUserId(storage) {
  if (!storage) return '';
  try {
    const user = JSON.parse(storage.getItem('user') || '{}');
    return String(user.id || user._id || '');
  } catch {
    return '';
  }
}

function scopedKey(baseKey, storage) {
  const userId = currentUserId(storage);
  return userId ? `${baseKey}:${userId}` : baseKey;
}

function isFullExperience(storage) {
  if (!storage) return false;
  try {
    const user = JSON.parse(storage.getItem('user') || '{}');
    return (user.experienceMode || user.preferences?.experienceMode) === 'full';
  } catch {
    return false;
  }
}

export function migrateLegacyProgress(storage = defaultStorage()) {
  if (!storage) return;
  try {
    const key = scopedKey(STORAGE_KEY, storage);
    const raw = storage.getItem(key) || storage.getItem(STORAGE_KEY);
    if (!raw) return;
    const p = JSON.parse(raw);
    if (p && p.schemaVersion === CURRENT_SCHEMA && p.steps && typeof p.steps === 'object') return;

    const next = { schemaVersion: CURRENT_SCHEMA, steps: {} };
    if (p && typeof p === 'object') {
      // Legacy numeric keys → slug
      for (const [legacy, slug] of Object.entries(LEGACY_TO_SLUG)) {
        if (p[legacy] === true) next.steps[slug] = true;
      }
      // Forward-compat: top-level non-numeric slug keys written by a newer client
      for (const k of Object.keys(p)) {
        if (!/^\d+$/.test(k) && k !== 'schemaVersion' && k !== 'steps' && p[k] === true) {
          next.steps[k] = true;
        }
      }
      // Partial-migration recovery: an already-present steps object
      if (p.steps && typeof p.steps === 'object') {
        for (const k of Object.keys(p.steps)) {
          if (p.steps[k] === true) next.steps[k] = true;
        }
      }
    }
    // R220 v3 → v4 backfill: existing users who had completed 'team' (or any
    // later step) shouldn't see the journey re-open with 'philosophy' marked
    // incomplete. Treat any prior progress as implicit philosophy completion.
    if (Object.keys(next.steps).length > 0 && !next.steps.philosophy) {
      next.steps.philosophy = true;
    }
    storage.setItem(key, JSON.stringify(next));
    if (key !== STORAGE_KEY) storage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore — corrupt JSON or storage disabled */
  }
}

export function readProgress(storage = defaultStorage()) {
  if (!storage) return {};
  try {
    const p = JSON.parse(storage.getItem(scopedKey(STORAGE_KEY, storage)) || '{}');
    return (p && p.steps && typeof p.steps === 'object') ? p.steps : {};
  } catch {
    return {};
  }
}

export function markJourneyStepComplete(slugOrSlugs, storage = defaultStorage()) {
  if (!storage) return;
  const slugs = Array.isArray(slugOrSlugs) ? slugOrSlugs : [slugOrSlugs];
  try {
    let p;
    const key = scopedKey(STORAGE_KEY, storage);
    try { p = JSON.parse(storage.getItem(key) || '{}'); } catch { p = {}; }
    if (!p || p.schemaVersion !== CURRENT_SCHEMA || !p.steps || typeof p.steps !== 'object') {
      p = { schemaVersion: CURRENT_SCHEMA, steps: (p && p.steps && typeof p.steps === 'object') ? p.steps : {} };
    }
    let changed = false;
    for (const s of slugs) {
      if (!p.steps[s]) {
        p.steps[s] = true;
        changed = true;
      }
    }
    if (changed) {
      storage.setItem(key, JSON.stringify(p));
      if (typeof window !== 'undefined') {
        try { window.dispatchEvent(new CustomEvent('journey-progress-updated')); } catch {}
      }
    }
  } catch {
    /* ignore */
  }
}

export function mergeServerProgress(serverSteps, storage = defaultStorage()) {
  if (!storage || !serverSteps || typeof serverSteps !== 'object') return {};
  const completed = Object.keys(serverSteps).filter((slug) => Boolean(serverSteps[slug]));
  const steps = Object.fromEntries(completed.map((slug) => [slug, true]));
  try {
    storage.setItem(
      scopedKey(STORAGE_KEY, storage),
      JSON.stringify({ schemaVersion: CURRENT_SCHEMA, steps })
    );
    if (typeof window !== 'undefined') {
      try { window.dispatchEvent(new CustomEvent('journey-progress-updated')); } catch {}
    }
  } catch {}
  return steps;
}

function readPending(storage = defaultStorage()) {
  if (!storage) return [];
  try {
    const value = JSON.parse(storage.getItem(scopedKey(PENDING_STORAGE_KEY, storage)) || '[]');
    return Array.isArray(value) ? value.filter((slug) => typeof slug === 'string') : [];
  } catch {
    return [];
  }
}

function queuePending(slug, storage = defaultStorage()) {
  if (!storage) return;
  const pending = new Set(readPending(storage));
  pending.add(slug);
  try { storage.setItem(scopedKey(PENDING_STORAGE_KEY, storage), JSON.stringify([...pending])); } catch {}
}

function removePending(slug, storage = defaultStorage()) {
  if (!storage) return;
  const pending = readPending(storage).filter((value) => value !== slug);
  try {
    const key = scopedKey(PENDING_STORAGE_KEY, storage);
    if (pending.length > 0) storage.setItem(key, JSON.stringify(pending));
    else storage.removeItem(key);
  } catch {}
}

export async function completeJourneyStep(slug, storage = defaultStorage()) {
  if (!slug) return false;
  if (isFullExperience(storage)) return true;
  const alreadyComplete = Boolean(readProgress(storage)[slug]);
  const pending = new Set(readPending(storage));
  if (alreadyComplete && !pending.has(slug)) return true;

  // Optimistic write — update localStorage immediately so the journey reflects
  // the completion on next open even if the server sync fails or is slow.
  markJourneyStepComplete(slug, storage);

  try {
    const response = await authAPI.updateOnboarding('completeStep', { step: slug });
    mergeServerProgress(response?.data?.onboarding?.journeySteps || {}, storage);
    removePending(slug, storage);
    return true;
  } catch (err) {
    console.warn('[Journey] Server sync failed for step:', slug, err?.response?.status || err?.message);
    queuePending(slug, storage);
    return false;
  }
}

export async function flushPendingJourneySteps(storage = defaultStorage()) {
  const pending = readPending(storage);
  for (const slug of pending) {
    try {
      const response = await authAPI.updateOnboarding('completeStep', { step: slug });
      mergeServerProgress(response?.data?.onboarding?.journeySteps || {}, storage);
      removePending(slug, storage);
    } catch {
      break;
    }
  }
}

// Module-load side effect: run migration eagerly so every consumer sees v2 shape.
// Idempotent by value — cross-tab safe (two tabs writing the same truth is a no-op).
migrateLegacyProgress();
